import { Middleware } from '@reduxjs/toolkit';
import { RootState } from './index';
import { SessionSyncManager } from '@/utils/sessionSync';

// Actions that should trigger session sync
const SYNC_ACTIONS = [
  // Tournament actions
  'tournament/createTournament',
  'tournament/loadTournament',
  'tournament/updateSettings',
  'tournament/setStatus',
  'tournament/setCurrentRound',
  'tournament/updateRounds',
  'tournament/addRound',
  'tournament/updateRound',
  'tournament/deleteRound',
  'tournament/updatePrizes',
  'tournament/addPrize',
  'tournament/updatePot',
  'tournament/updateRake',
  'tournament/addHistoryEntry',
  'tournament/setStartTime',
  'tournament/setEndTime',
  
  // Clock actions
  'clock/startClock',
  'clock/stopClock',
  'clock/toggleClock',
  'clock/tick',
  'clock/setSecondsRemaining',
  'clock/resetClock',
  'clock/setCurrentRound',
  'clock/setIsBreak',
  'clock/nextRound',
  'clock/startBreak',
  'clock/endBreak',
  'clock/startHandTimer',
  'clock/stopHandTimer',
  'clock/setHandTimerSeconds',
  
  // Player actions
  'players/addPlayer',
  'players/addPlayers',
  'players/updatePlayer',
  'players/deletePlayer',
  'players/deleteAllPlayers',
  'players/eliminatePlayer',
  'players/reinstatePlayer',
  'players/addRebuy',
  'players/addAddOn',
  'players/setPaid',
  'players/setAllPaid',
  'players/assignSeats',
  'players/movePlayer',
  'players/updateRankings',
  'players/loadPlayers',
  
  // Settings actions
  'settings/updateSetting',
  'settings/loadSettings',
];

let sessionSyncManager: SessionSyncManager | null = null;
let isReceivingExternalUpdate = false;

export const createSessionMiddleware = (): Middleware => {
  return (store) => (next) => (action) => {
    // Initialize session sync manager on first action
    if (!sessionSyncManager) {
      sessionSyncManager = new SessionSyncManager();
      
      // Set up callback for receiving state updates from other windows
      sessionSyncManager.setStateUpdateCallback((state) => {
        isReceivingExternalUpdate = true;
        
        // Load the state into Redux store
        if (state.tournament.current) {
          store.dispatch({ type: 'tournament/loadTournament', payload: state.tournament.current });
        }
        
        // Update clock state - use specific actions that exist
        if (state.clock.isRunning !== undefined) {
          if (state.clock.isRunning) {
            store.dispatch({ type: 'clock/startClock' });
          } else {
            store.dispatch({ type: 'clock/stopClock' });
          }
        }
        if (state.clock.secondsRemaining !== undefined) {
          store.dispatch({ type: 'clock/setSecondsRemaining', payload: state.clock.secondsRemaining });
        }
        if (state.clock.currentRound !== undefined) {
          store.dispatch({ type: 'clock/setCurrentRound', payload: state.clock.currentRound });
        }
        if (state.clock.isBreak !== undefined) {
          store.dispatch({ type: 'clock/setIsBreak', payload: state.clock.isBreak });
        }
        if (state.clock.handTimerRunning !== undefined && state.clock.handTimerSeconds !== undefined) {
          if (state.clock.handTimerRunning) {
            store.dispatch({ type: 'clock/startHandTimer', payload: state.clock.handTimerSeconds });
          } else {
            store.dispatch({ type: 'clock/stopHandTimer' });
          }
        }
        
        // Update players state
        store.dispatch({ type: 'players/loadPlayers', payload: state.players.players });
        store.dispatch({ type: 'players/setFilter', payload: state.players.filter });
        
        // Update settings state
        store.dispatch({ type: 'settings/loadSettings', payload: state.settings });
        
        isReceivingExternalUpdate = false;
      });
    }

    // Process the action
    const result = next(action);

    // Only sync state for relevant actions and not when receiving external updates
    if (!isReceivingExternalUpdate && SYNC_ACTIONS.includes(action.type)) {
      const state = store.getState();
      sessionSyncManager?.broadcastState({
        tournament: state.tournament,
        clock: state.clock,
        players: state.players,
        settings: state.settings,
      });
    }

    return result;
  };
};

// Cleanup function to be called when the app unmounts
export const cleanupSessionSync = () => {
  if (sessionSyncManager) {
    sessionSyncManager.destroy();
    sessionSyncManager = null;
  }
};